import Hero from "../components/Hero";
import ServiceCard from "../components/ServiceCard";
import TechStack from "../components/TechStack";
import { motion } from "framer-motion";
import {
  Code2,
  Smartphone,
  Globe2,
  Server,
  ShieldCheck,
  Cpu,
  Users,
  Bot,
} from "lucide-react";
import FAQSection from "./FAQSection";
import CustomerFeedback from "../components/CustomerFeedback";

const services = [
  {
    number: "01",
    icon: Code2,
    title: "Custom Software",
    tag: "Enterprise",
    text: "Powerful ERP, CRM, HRMS, billing, inventory and custom business management platforms.",
  },
  {
    number: "02",
    icon: Smartphone,
    title: "Mobile App Development",
    tag: "Mobile",
    text: "Scalable Android and iOS applications designed for customers, teams and modern businesses.",
  },
  {
    number: "03",
    icon: Globe2,
    title: "Web Development",
    tag: "Digital",
    text: "High-performance corporate websites, web applications, portals and customer experiences.",
  },
  {
    number: "04",
    icon: Server,
    title: "Cloud & API Solutions",
    tag: "Infrastructure",
    text: "Secure backend systems, databases, APIs, integrations and cloud-ready application architecture.",
  },
  {
    number: "05",
    icon: ShieldCheck,
    title: "Security & Protection",
    tag: "Security",
    text: "Secure authentication, role-based access, data protection, backups and reliable infrastructure.",
  },
  {
    number: "06",
    icon: Cpu,
    title: "AI & Automation",
    tag: "Artificial Intelligence",
    text: "AI-powered dashboards, intelligent workflows, business insights and automation systems.",
  },
];


export default function Home() {
  return (
    <>
      <Hero />
      <section className="relative w-full border-y border-white/5 bg-white/[0.015] lg:w-[100%]">
        <div className="relative w-full lg:w-[80%] mx-auto px-4 sm:px-6 py-10">
          <div className="absolute left-1/2 top-1/2 -z-10 h-40 w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/10 blur-[100px]" />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              {
                value: "100+",
                label: "Projects Delivered",
                sub: "Proven digital expertise",
                Icon: Users,
              },
              {
                value: "AI",
                label: "Driven Innovation",
                sub: "Smart software solutions",
                Icon: Bot,
              },
              {
                value: "24/7",
                label: "Technical Support",
                sub: "Reliable assistance",
                Icon: ShieldCheck,
              },
              {
                value: "Cloud",
                label: "Scalable Technology",
                sub: "Secure cloud solutions",
                Icon: Server,
              },
            ].map(({ value, label, sub, Icon }, index) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-[1.7rem] border border-white/10 bg-white/[0.055] p-5 sm:p-6 backdrop-blur-xl transition-all duration-300 hover:border-pink-500/30 hover:bg-white/[0.09] hover:shadow-[0_20px_60px_rgba(236,72,153,0.12)]"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pink-500/70 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />

                <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-pink-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="relative flex items-center gap-4">
                  <div className="relative shrink-0">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500 to-violet-700 blur-md opacity-30 group-hover:opacity-60 transition-opacity" />

                    <div className="relative grid h-12 w-12 sm:h-14 sm:w-14 place-items-center rounded-2xl border border-white/10 bg-gradient-to-br from-pink-500/90 to-violet-700/90 shadow-lg">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="text-xl sm:text-2xl font-black tracking-tight text-white">
                      {value}
                    </div>

                    <div className="mt-0.5 text-xs sm:text-sm font-semibold text-pink-200">
                      {label}
                    </div>

                    <div className="mt-1 hidden sm:block text-[10px] tracking-wide text-slate-500">
                      {sub}
                    </div>
                  </div>
                </div>

                <div className="relative mt-5 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Active
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full lg:w-[80%] mx-auto px-4 sm:px-6 py-10">

        <div className="absolute left-1/2 top-20 -z-10 h-64 w-[60%] -translate-x-1/2 rounded-full bg-violet-600/10 blur-[120px]" />

        <div className="mx-auto mb-14 max-w-3xl text-center">

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-500/20 bg-pink-500/5 px-4 py-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pink-400" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-300">
              What We Build
            </span>
          </div>

          <h2 className="text-3xl font-black tracking-tight text-white sm:text-5xl">
            Corporate IT{" "}
            <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              Services
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
            From strategy and design to development, deployment and ongoing support,
            we build intelligent technology solutions designed around your business.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard
              key={service.title}
              {...service}
            />
          ))}
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] lg:w-[100%]">
        <div className="relative w-full lg:w-[80%] mx-auto px-4 sm:px-6 py-10">

          <div className="absolute left-1/2 top-1/2 -z-10 h-72 w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-600/10 blur-[130px]" />

          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-pink-500/[0.10] via-violet-700/[0.10] to-white/[0.025] p-6 shadow-[0_30px_100px_rgba(0,0,0,0.25)] backdrop-blur-xl sm:p-10">

            <div
              className="pointer-events-none absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />

            <div className="relative z-10 text-center">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-500/20 bg-pink-500/5 px-4 py-2">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pink-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-300">
                  Our Technology
                </span>
              </div>

              <h2 className="text-3xl font-black tracking-tight text-white sm:text-5xl">
                Latest{" "}
                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                  Technology Stack
                </span>
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
                We leverage modern frameworks, cloud platforms, databases,
                AI technologies and development tools to create secure,
                scalable and future-ready software.
              </p>
            </div>

            <div className="relative z-10 mt-10">
              <TechStack />
            </div>

          </div>
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] lg:w-[100%]">
        <FAQSection />
      </section>

      <CustomerFeedback />
    </>
  );
}